import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Box, Sphere, Cylinder, RoundedBox, Html, Environment, Float } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

// Responsive hook to detect screen size
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
    isTablet: typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth < 1024 : false,
    isDesktop: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

// Floating particles for atmosphere
const FloatingParticle = ({ position }) => {
  const particleRef = useRef();

  useFrame((state) => {
    if (particleRef.current) {
      particleRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime + position[0]) * 0.3;
      particleRef.current.position.x = position[0] + Math.cos(state.clock.elapsedTime * 0.5) * 0.2;
    }
  });

  return (
    <Sphere ref={particleRef} args={[0.02, 8, 8]} position={position}>
      <meshStandardMaterial
        color="#60A5FA"
        emissive="#60A5FA"
        emissiveIntensity={2}
        toneMapped={false}
      />
    </Sphere>
  );
};

// RGB Fan Component
const RGBFan = ({ position, rotation = [0, 0, 0] }) => {
  const fanRef = useRef();

  useFrame((state) => {
    if (fanRef.current) {
      fanRef.current.rotation.z = state.clock.elapsedTime * 3;
    }
  });

  return (
    <group position={position} rotation={rotation}>
      {/* Fan ring */}
      <mesh>
        <torusGeometry args={[0.12, 0.02, 16, 32]} />
        <meshStandardMaterial
          color="#1F2937"
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
      {/* RGB glow */}
      <mesh>
        <torusGeometry args={[0.12, 0.015, 16, 32]} />
        <meshStandardMaterial
          color="#8B5CF6"
          emissive="#8B5CF6"
          emissiveIntensity={2}
          toneMapped={false}
        />
      </mesh>
      {/* Fan blades */}
      <group ref={fanRef}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            args={[0.15, 0.03, 0.005]}
            position={[
              Math.cos((i * Math.PI) / 2) * 0.06,
              Math.sin((i * Math.PI) / 2) * 0.06,
              0
            ]}
            rotation={[0, 0, (i * Math.PI) / 2]}
          >
            <meshStandardMaterial color="#374151" />
          </Box>
        ))}
      </group>
    </group>
  );
};

// Gaming PC Case
const PCCase = ({ isMobile }) => {
  const scale = isMobile ? 0.7 : 1;

  return (
    <group position={[1.2, -0.3, 0.3]} scale={scale}>
      {/* Main case body */}
      <RoundedBox args={[0.5, 0.8, 0.45]} radius={0.02}>
        <meshStandardMaterial
          color="#111827"
          metalness={0.7}
          roughness={0.3}
        />
      </RoundedBox>

      {/* Glass side panel with RGB glow */}
      <Box args={[0.02, 0.75, 0.4]} position={[-0.24, 0, 0]}>
        <meshStandardMaterial
          color="#1F2937"
          transparent={true}
          opacity={0.3}
          metalness={0.9}
          roughness={0.1}
        />
      </Box>

      {/* RGB strip inside */}
      <Box args={[0.02, 0.7, 0.35]} position={[-0.23, 0, 0]}>
        <meshStandardMaterial
          color="#8B5CF6"
          emissive="#8B5CF6"
          emissiveIntensity={1}
          toneMapped={false}
        />
      </Box>

      {/* Front RGB fans */}
      <RGBFan position={[0.26, 0.2, 0]} rotation={[0, Math.PI / 2, 0]} />
      <RGBFan position={[0.26, -0.2, 0]} rotation={[0, Math.PI / 2, 0]} />

      {/* Top RGB fan */}
      <RGBFan position={[0, 0.42, 0.1]} rotation={[Math.PI / 2, 0, 0]} />

      {/* Power button */}
      <Cylinder args={[0.015, 0.015, 0.01, 16]} position={[0.26, 0.35, 0.15]} rotation={[0, 0, Math.PI / 2]}>
        <meshStandardMaterial
          color="#3B82F6"
          emissive="#3B82F6"
          emissiveIntensity={2}
          toneMapped={false}
        />
      </Cylinder>

      {/* GPU RGB strip (bottom) */}
      <Box args={[0.35, 0.02, 0.08]} position={[0, -0.25, 0.15]}>
        <meshStandardMaterial
          color="#EC4899"
          emissive="#EC4899"
          emissiveIntensity={1.5}
          toneMapped={false}
        />
      </Box>
    </group>
  );
};

// Monitor with code display
const CentralMonitor = ({ isMobile }) => {
  const scale = isMobile ? 0.7 : 1;

  return (
    <group position={[0, 0.1, 0]} scale={scale}>
      {/* Monitor stand base */}
      <Cylinder args={[0.15, 0.15, 0.02, 32]} position={[0, -0.5, 0]}>
        <meshStandardMaterial color="#1F2937" metalness={0.8} roughness={0.2} />
      </Cylinder>

      {/* Monitor stand neck */}
      <Cylinder args={[0.03, 0.03, 0.4, 16]} position={[0, -0.3, 0]}>
        <meshStandardMaterial color="#374151" metalness={0.7} roughness={0.3} />
      </Cylinder>

      {/* Monitor back */}
      <RoundedBox args={[1.2, 0.8, 0.05]} radius={0.02} position={[0, 0.1, -0.03]}>
        <meshStandardMaterial color="#1F2937" metalness={0.6} roughness={0.4} />
      </RoundedBox>

      {/* Monitor frame */}
      <RoundedBox args={[1.2, 0.8, 0.03]} radius={0.02} position={[0, 0.1, 0]}>
        <meshStandardMaterial color="#111827" metalness={0.8} roughness={0.2} />
      </RoundedBox>

      {/* Monitor screen - dark with code */}
      <Box args={[1.15, 0.75, 0.01]} position={[0, 0.1, 0.02]}>
        <meshStandardMaterial
          color="#0F172A"
          emissive="#1E293B"
          emissiveIntensity={0.3}
        />
      </Box>

      {/* Code text simulation with colored rectangles */}
      <group position={[0, 0.1, 0.03]}>
        {/* VS Code style colored code lines */}
        {[-0.3, -0.15, 0, 0.15, 0.25].map((y, i) => (
          <Box
            key={i}
            args={[0.8, 0.02, 0.001]}
            position={[-0.15, y, 0]}
          >
            <meshStandardMaterial
              color={['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'][i]}
              emissive={['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'][i]}
              emissiveIntensity={0.5}
              toneMapped={false}
            />
          </Box>
        ))}
      </group>

      {/* RGB Monitor backlight */}
      <Box args={[1.3, 0.85, 0.01]} position={[0, 0.1, -0.06]}>
        <meshStandardMaterial
          color="#8B5CF6"
          emissive="#8B5CF6"
          emissiveIntensity={0.5}
          transparent={true}
          opacity={0.3}
          toneMapped={false}
        />
      </Box>
    </group>
  );
};

// RGB Keyboard
const Keyboard = ({ isMobile }) => {
  const scale = isMobile ? 0.7 : 1;

  return (
    <group position={[0, -0.42, 0.5]} scale={scale}>
      {/* Keyboard base */}
      <RoundedBox args={[0.9, 0.05, 0.35]} radius={0.01}>
        <meshStandardMaterial color="#1F2937" metalness={0.6} roughness={0.4} />
      </RoundedBox>

      {/* Keys with RGB underglow */}
      {[...Array(50)].map((_, i) => (
        <Box
          key={i}
          args={[0.035, 0.015, 0.035]}
          position={[
            -0.4 + (i % 15) * 0.055,
            0.03,
            0.12 - Math.floor(i / 15) * 0.045
          ]}
        >
          <meshStandardMaterial color="#374151" />
        </Box>
      ))}

      {/* RGB underglow */}
      <Box args={[0.95, 0.01, 0.4]} position={[0, -0.03, 0]}>
        <meshStandardMaterial
          color="#8B5CF6"
          emissive="#8B5CF6"
          emissiveIntensity={1.5}
          toneMapped={false}
          transparent={true}
          opacity={0.6}
        />
      </Box>
    </group>
  );
};

// RGB Mouse
const Mouse = ({ isMobile }) => {
  const scale = isMobile ? 0.7 : 1;

  return (
    <group position={[0.6, -0.42, 0.5]} scale={scale}>
      {/* Mouse body */}
      <Sphere args={[0.06, 16, 16]} scale={[1, 0.6, 1.3]}>
        <meshStandardMaterial color="#1F2937" metalness={0.7} roughness={0.3} />
      </Sphere>

      {/* RGB scroll wheel */}
      <Cylinder args={[0.008, 0.008, 0.04, 16]} position={[0, 0.02, -0.02]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial
          color="#EC4899"
          emissive="#EC4899"
          emissiveIntensity={2}
          toneMapped={false}
        />
      </Cylinder>

      {/* RGB underglow */}
      <Sphere args={[0.055, 16, 16]} scale={[1, 0.3, 1.3]} position={[0, -0.02, 0]}>
        <meshStandardMaterial
          color="#3B82F6"
          emissive="#3B82F6"
          emissiveIntensity={1.5}
          toneMapped={false}
          transparent={true}
          opacity={0.5}
        />
      </Sphere>
    </group>
  );
};

// RGB Speakers
const Speaker = ({ position, isMobile }) => {
  const scale = isMobile ? 0.6 : 1;

  return (
    <group position={position} scale={scale}>
      {/* Speaker body */}
      <Cylinder args={[0.08, 0.1, 0.25, 32]}>
        <meshStandardMaterial color="#1F2937" metalness={0.6} roughness={0.4} />
      </Cylinder>

      {/* Speaker cone */}
      <Cylinder args={[0.06, 0.08, 0.02, 32]} position={[0, 0.08, 0]}>
        <meshStandardMaterial color="#374151" />
      </Cylinder>

      {/* RGB ring */}
      <mesh position={[0, -0.05, 0]}>
        <torusGeometry args={[0.09, 0.01, 16, 32]} />
        <meshStandardMaterial
          color="#8B5CF6"
          emissive="#8B5CF6"
          emissiveIntensity={2}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
};

// Desk surface
const DeskSurface = ({ isMobile }) => {
  const scale = isMobile ? 0.8 : 1;

  return (
    <group position={[0, -0.5, 0]} scale={scale}>
      <RoundedBox args={[3, 0.05, 1.5]} radius={0.02}>
        <meshStandardMaterial
          color="#1F2937"
          metalness={0.3}
          roughness={0.7}
        />
      </RoundedBox>

      {/* RGB mousepad */}
      <Box args={[0.6, 0.001, 0.4]} position={[0.3, 0.03, 0.5]}>
        <meshStandardMaterial
          color="#EC4899"
          emissive="#EC4899"
          emissiveIntensity={0.5}
          toneMapped={false}
        />
      </Box>
    </group>
  );
};

// Complete Desktop Setup
const DesktopSetup = ({ isMobile }) => {
  return (
    <Float speed={0.5} rotationIntensity={0.1} floatIntensity={0.2}>
      <group>
        <DeskSurface isMobile={isMobile} />
        <CentralMonitor isMobile={isMobile} />
        <PCCase isMobile={isMobile} />
        <Keyboard isMobile={isMobile} />
        <Mouse isMobile={isMobile} />
        <Speaker position={[-0.8, -0.2, 0.3]} isMobile={isMobile} />
        <Speaker position={[0.8, -0.2, 0.3]} isMobile={isMobile} />
      </group>
    </Float>
  );
};

// Laptop component - Floating
const Laptop = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const laptopRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (laptopRef.current) {
      laptopRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        laptopRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.5}>
      <group
        ref={laptopRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        {/* Laptop base */}
        <RoundedBox args={[1.2, 0.05, 0.9]} radius={0.02}>
          <meshStandardMaterial
            color={isActive ? "#1E293B" : "#0F172A"}
            metalness={0.8}
            roughness={0.2}
            emissive={isActive ? "#3B82F6" : "#000000"}
            emissiveIntensity={isActive ? 0.5 : 0}
          />
        </RoundedBox>

        {/* Laptop screen */}
        <RoundedBox args={[1.2, 0.8, 0.03]} radius={0.02} position={[0, 0.4, -0.4]} rotation={[-0.2, 0, 0]}>
          <meshStandardMaterial
            color="#3B82F6"
            emissive="#3B82F6"
            emissiveIntensity={isActive ? 1.5 : 0.8}
            toneMapped={false}
          />
        </RoundedBox>

        <Html position={[0, 0.4, -0.38]} rotation={[-0.2, 0, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            💻 Frontend
          </div>
        </Html>
      </group>
    </Float>
  );
};

// Monitor component - Floating
const Monitor = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const monitorRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (monitorRef.current) {
      monitorRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        monitorRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.8} rotationIntensity={0.4} floatIntensity={0.6}>
      <group
        ref={monitorRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        {/* Monitor frame */}
        <RoundedBox args={[1.2, 0.9, 0.08]} radius={0.03}>
          <meshStandardMaterial
            color={isActive ? "#1E293B" : "#0F172A"}
            metalness={0.8}
            roughness={0.2}
            emissive={isActive ? "#10B981" : "#000000"}
            emissiveIntensity={isActive ? 0.5 : 0}
          />
        </RoundedBox>

        {/* Monitor screen */}
        <Box args={[1.1, 0.8, 0.02]} position={[0, 0, 0.05]}>
          <meshStandardMaterial
            color="#10B981"
            emissive="#10B981"
            emissiveIntensity={isActive ? 1.5 : 0.8}
            toneMapped={false}
          />
        </Box>

        <Html position={[0, 0, 0.07]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            ⚙️ Backend
          </div>
        </Html>
      </group>
    </Float>
  );
};

// APIs Component - Interconnected nodes
const APIs = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const apiRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (apiRef.current) {
      apiRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        apiRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.8} rotationIntensity={0.4} floatIntensity={0.6}>
      <group
        ref={apiRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        {/* Central hub */}
        <Sphere args={[0.15, 32, 32]}>
          <meshStandardMaterial
            color={isActive ? "#10B981" : "#059669"}
            metalness={0.8}
            roughness={0.2}
            emissive={isActive ? "#10B981" : "#000000"}
            emissiveIntensity={isActive ? 0.8 : 0}
          />
        </Sphere>

        {/* API endpoint nodes */}
        {[0, 1, 2, 3].map((i) => {
          const angle = (i * Math.PI) / 2;
          const radius = 0.35;
          return (
            <group key={i}>
              {/* Connection line */}
              <Box
                args={[0.02, radius, 0.02]}
                position={[
                  Math.cos(angle) * (radius / 2),
                  Math.sin(angle) * (radius / 2),
                  0
                ]}
                rotation={[0, 0, -angle]}
              >
                <meshStandardMaterial
                  color="#10B981"
                  emissive="#10B981"
                  emissiveIntensity={isActive ? 0.5 : 0.2}
                  toneMapped={false}
                />
              </Box>
              {/* Endpoint sphere */}
              <Sphere
                args={[0.08, 16, 16]}
                position={[Math.cos(angle) * radius, Math.sin(angle) * radius, 0]}
              >
                <meshStandardMaterial
                  color="#34D399"
                  emissive="#34D399"
                  emissiveIntensity={isActive ? 1 : 0.5}
                  toneMapped={false}
                />
              </Sphere>
            </group>
          );
        })}

        <Html position={[0, -0.5, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            🔌 APIs
          </div>
        </Html>
      </group>
    </Float>
  );
};

// Programming Component - Code brackets
const Programming = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const codeRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (codeRef.current) {
      codeRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        codeRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={0.7}>
      <group
        ref={codeRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        {/* Left bracket { */}
        <group position={[-0.2, 0, 0]}>
          <Box args={[0.05, 0.15, 0.05]} position={[0, 0.2, 0]}>
            <meshStandardMaterial
              color={isActive ? "#F59E0B" : "#D97706"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#F59E0B" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.05, 0.15, 0.05]} position={[0, -0.2, 0]}>
            <meshStandardMaterial
              color={isActive ? "#F59E0B" : "#D97706"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#F59E0B" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.15, 0.05, 0.05]} position={[-0.05, 0.3, 0]}>
            <meshStandardMaterial
              color={isActive ? "#F59E0B" : "#D97706"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#F59E0B" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.15, 0.05, 0.05]} position={[-0.05, -0.3, 0]}>
            <meshStandardMaterial
              color={isActive ? "#F59E0B" : "#D97706"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#F59E0B" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
        </group>

        {/* Right bracket } */}
        <group position={[0.2, 0, 0]}>
          <Box args={[0.05, 0.15, 0.05]} position={[0, 0.2, 0]}>
            <meshStandardMaterial
              color={isActive ? "#FBBF24" : "#F59E0B"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#FBBF24" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.05, 0.15, 0.05]} position={[0, -0.2, 0]}>
            <meshStandardMaterial
              color={isActive ? "#FBBF24" : "#F59E0B"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#FBBF24" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.15, 0.05, 0.05]} position={[0.05, 0.3, 0]}>
            <meshStandardMaterial
              color={isActive ? "#FBBF24" : "#F59E0B"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#FBBF24" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
          <Box args={[0.15, 0.05, 0.05]} position={[0.05, -0.3, 0]}>
            <meshStandardMaterial
              color={isActive ? "#FBBF24" : "#F59E0B"}
              metalness={0.7}
              roughness={0.3}
              emissive={isActive ? "#FBBF24" : "#000000"}
              emissiveIntensity={isActive ? 0.5 : 0}
            />
          </Box>
        </group>

        {/* Code lines in center */}
        {[-0.1, 0, 0.1].map((y, i) => (
          <Box key={i} args={[0.25, 0.02, 0.02]} position={[0, y, 0]}>
            <meshStandardMaterial
              color="#FCD34D"
              emissive="#FCD34D"
              emissiveIntensity={isActive ? 1 : 0.5}
              toneMapped={false}
            />
          </Box>
        ))}

        <Html position={[0, -0.5, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            💻 Code
          </div>
        </Html>
      </group>
    </Float>
  );
};

// WordPress Component - W logo style
const WordPress = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const wpRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (wpRef.current) {
      wpRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        wpRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.6} rotationIntensity={0.4} floatIntensity={0.5}>
      <group
        ref={wpRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        {/* Circle background */}
        <Cylinder args={[0.3, 0.3, 0.05, 32]}>
          <meshStandardMaterial
            color={isActive ? "#0891B2" : "#0E7490"}
            metalness={0.7}
            roughness={0.3}
            emissive={isActive ? "#06B6D4" : "#000000"}
            emissiveIntensity={isActive ? 0.5 : 0}
          />
        </Cylinder>

        {/* W letter - simplified geometric representation */}
        <group position={[0, 0, 0.03]}>
          {/* Left vertical */}
          <Box args={[0.04, 0.3, 0.04]} position={[-0.15, 0, 0]}>
            <meshStandardMaterial
              color="#FFFFFF"
              emissive="#FFFFFF"
              emissiveIntensity={isActive ? 0.3 : 0.1}
            />
          </Box>
          {/* Left middle diagonal */}
          <Box args={[0.04, 0.25, 0.04]} position={[-0.07, 0, 0]} rotation={[0, 0, -0.3]}>
            <meshStandardMaterial
              color="#FFFFFF"
              emissive="#FFFFFF"
              emissiveIntensity={isActive ? 0.3 : 0.1}
            />
          </Box>
          {/* Center vertical */}
          <Box args={[0.04, 0.2, 0.04]} position={[0, -0.05, 0]}>
            <meshStandardMaterial
              color="#FFFFFF"
              emissive="#FFFFFF"
              emissiveIntensity={isActive ? 0.3 : 0.1}
            />
          </Box>
          {/* Right middle diagonal */}
          <Box args={[0.04, 0.25, 0.04]} position={[0.07, 0, 0]} rotation={[0, 0, 0.3]}>
            <meshStandardMaterial
              color="#FFFFFF"
              emissive="#FFFFFF"
              emissiveIntensity={isActive ? 0.3 : 0.1}
            />
          </Box>
          {/* Right vertical */}
          <Box args={[0.04, 0.3, 0.04]} position={[0.15, 0, 0]}>
            <meshStandardMaterial
              color="#FFFFFF"
              emissive="#FFFFFF"
              emissiveIntensity={isActive ? 0.3 : 0.1}
            />
          </Box>
        </group>

        {/* Outer ring glow */}
        <mesh>
          <torusGeometry args={[0.32, 0.02, 16, 32]} />
          <meshStandardMaterial
            color="#22D3EE"
            emissive="#22D3EE"
            emissiveIntensity={isActive ? 1.5 : 0.5}
            toneMapped={false}
          />
        </mesh>

        <Html position={[0, -0.5, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            🌐 WordPress
          </div>
        </Html>
      </group>
    </Float>
  );
};

// Database component - Floating
const Database = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const dbRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (dbRef.current) {
      dbRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        dbRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.6} rotationIntensity={0.4} floatIntensity={0.5}>
      <group
        ref={dbRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        <Cylinder args={[0.3, 0.3, 0.15, 32]}>
          <meshStandardMaterial
            color={isActive ? "#F59E0B" : "#D97706"}
            metalness={0.7}
            roughness={0.3}
            emissive={isActive ? "#F59E0B" : "#000000"}
            emissiveIntensity={isActive ? 0.5 : 0}
          />
        </Cylinder>
        <Cylinder args={[0.3, 0.3, 0.15, 32]} position={[0, 0.15, 0]}>
          <meshStandardMaterial
            color={isActive ? "#FBBF24" : "#F59E0B"}
            metalness={0.7}
            roughness={0.3}
          />
        </Cylinder>

        <Html position={[0, 0.3, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            💾 Database
          </div>
        </Html>
      </group>
    </Float>
  );
};

// Cloud component - Floating
const Cloud = ({ onClick, isActive, position, isMobile, isDarkMode }) => {
  const cloudRef = useRef();
  const scale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    if (cloudRef.current) {
      cloudRef.current.rotation.y = state.clock.elapsedTime * 0.3;
      if (isActive) {
        cloudRef.current.scale.setScalar(scale * (1 + Math.sin(state.clock.elapsedTime * 2) * 0.1));
      }
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={0.3} floatIntensity={0.6}>
      <group
        ref={cloudRef}
        position={position}
        scale={scale}
        onClick={onClick}
        onPointerOver={() => document.body.style.cursor = 'pointer'}
        onPointerOut={() => document.body.style.cursor = 'default'}
      >
        <Sphere args={[0.3, 32, 32]} position={[0, 0, 0]}>
          <meshStandardMaterial
            color={isActive ? "#06B6D4" : "#0891B2"}
            metalness={0.6}
            roughness={0.4}
            emissive={isActive ? "#06B6D4" : "#000000"}
            emissiveIntensity={isActive ? 0.5 : 0}
          />
        </Sphere>
        <Sphere args={[0.25, 32, 32]} position={[0.25, 0.1, 0]}>
          <meshStandardMaterial color={isActive ? "#22D3EE" : "#06B6D4"} metalness={0.6} roughness={0.4} />
        </Sphere>
        <Sphere args={[0.2, 32, 32]} position={[-0.25, 0.05, 0]}>
          <meshStandardMaterial color={isActive ? "#22D3EE" : "#06B6D4"} metalness={0.6} roughness={0.4} />
        </Sphere>

        <Html position={[0, 0.5, 0]} center transform distanceFactor={8}>
          <div style={{
            color: isDarkMode ? 'white' : '#1F2937',
            fontSize: isMobile ? '8px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}>
            ☁️ Cloud
          </div>
        </Html>
      </group>
    </Float>
  );
};

// Main 3D Scene
const Scene = ({ activeObject, setActiveObject, isMobile, isTablet, isDarkMode }) => {
  // Responsive object positions
  const getPositions = () => {
    if (isMobile) {
      return {
        laptop: [0, 1.5, 0],
        monitor: [1.2, 0.8, 0.5],
        apis: [-1.2, 0.8, 0.5],
        programming: [1.2, -0.3, -0.5],
        wordpress: [-1.2, -0.3, -0.5],
        database: [0.8, -1, -0.3],
        cloud: [-0.8, -1, -0.3]
      };
    } else if (isTablet) {
      return {
        laptop: [2, 1, 0],
        monitor: [1.5, -0.5, 1.5],
        apis: [-2, 1, 0],
        programming: [2, -0.5, -1.5],
        wordpress: [-2, -0.5, -1.5],
        database: [-1.5, -0.5, 1.5],
        cloud: [0, -1.5, 0]
      };
    } else {
      return {
        laptop: [3, 1.5, 0],
        monitor: [2, -1, 2],
        apis: [-3, 1.5, 0],
        programming: [3, -1, -2],
        wordpress: [-3, -1, -2],
        database: [-2, -1, 2],
        cloud: [0, -2, 0]
      };
    }
  };

  const positions = getPositions();

  return (
    <>
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} intensity={1} />
      <pointLight position={[-5, 3, -3]} intensity={0.5} color="#60A5FA" />
      <spotLight position={[0, 5, 2]} angle={0.6} penumbra={1} intensity={1.5} castShadow />
      <Environment preset="city" />

      <DesktopSetup isMobile={isMobile} />

      <Laptop
        onClick={() => setActiveObject('laptop')}
        isActive={activeObject === 'laptop'}
        position={positions.laptop}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <Monitor
        onClick={() => setActiveObject('monitor')}
        isActive={activeObject === 'monitor'}
        position={positions.monitor}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <APIs
        onClick={() => setActiveObject('apis')}
        isActive={activeObject === 'apis'}
        position={positions.apis}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <Programming
        onClick={() => setActiveObject('programming')}
        isActive={activeObject === 'programming'}
        position={positions.programming}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <WordPress
        onClick={() => setActiveObject('wordpress')}
        isActive={activeObject === 'wordpress'}
        position={positions.wordpress}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <Database
        onClick={() => setActiveObject('database')}
        isActive={activeObject === 'database'}
        position={positions.database}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />
      <Cloud
        onClick={() => setActiveObject('cloud')}
        isActive={activeObject === 'cloud'}
        position={positions.cloud}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
      />

      {/* Atmospheric particles */}
      {!isMobile && [...Array(20)].map((_, i) => (
        <FloatingParticle
          key={i}
          position={[
            Math.random() * 8 - 4,
            Math.random() * 4 - 2,
            Math.random() * 4 - 2
          ]}
        />
      ))}

      <OrbitControls
        enableZoom={true}
        enablePan={false}
        minDistance={isMobile ? 4 : 6}
        maxDistance={isMobile ? 10 : 15}
        maxPolarAngle={Math.PI / 1.8}
        target={[0, 0, 0]}
        enableDamping
        dampingFactor={0.05}
        autoRotate={!isMobile}
        autoRotateSpeed={0.5}
      />
    </>
  );
};

// Object info data
const objectInfo = {
  laptop: {
    title: "Frontend Development",
    icon: "💻",
    color: "#3B82F6",
    description: "Building modern, responsive web applications with React, TypeScript, and Tailwind CSS",
    projects: ["E-commerce Platform", "Social Media Dashboard", "Analytics Tool"]
  },
  monitor: {
    title: "Backend Development",
    icon: "⚙️",
    color: "#10B981",
    description: "Creating scalable APIs and microservices with Node.js, Express, and databases",
    projects: ["REST API Gateway", "Real-time Chat Server", "Payment Processing System"]
  },
  apis: {
    title: "API Development",
    icon: "🔌",
    color: "#10B981",
    description: "Designing and implementing RESTful APIs, GraphQL endpoints, and microservices architecture",
    projects: ["RESTful API Design", "GraphQL Integration", "API Security & Authentication"]
  },
  programming: {
    title: "Programming Languages",
    icon: "💻",
    color: "#F59E0B",
    description: "Proficient in multiple programming languages and paradigms for versatile development",
    projects: ["JavaScript/TypeScript", "Python", "Java", "C++"]
  },
  wordpress: {
    title: "WordPress Development",
    icon: "🌐",
    color: "#06B6D4",
    description: "Custom WordPress theme and plugin development with modern PHP practices",
    projects: ["Custom Themes", "Plugin Development", "WooCommerce Integration"]
  },
  database: {
    title: "Database Management",
    icon: "💾",
    color: "#F59E0B",
    description: "Designing and optimizing databases with MongoDB, PostgreSQL, and Redis",
    projects: ["Data Modeling", "Query Optimization", "Caching Strategies"]
  },
  cloud: {
    title: "Cloud & DevOps",
    icon: "☁️",
    color: "#06B6D4",
    description: "Deploying and managing applications on AWS, Docker, and CI/CD pipelines",
    projects: ["AWS Infrastructure", "Docker Containers", "GitHub Actions"]
  }
};

// Main Component
const Workspace3D = () => {
  const [activeObject, setActiveObject] = useState(null);
  const { isMobile, isTablet } = useResponsive();
  const { theme } = useTheme();

  // Check if we're in dark mode
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Responsive camera position
  const getCameraPosition = () => {
    if (isMobile) return [0, 0, 6];
    if (isTablet) return [0, 0, 8];
    return [0, 1, 10];
  };

  return (
    <section className={`relative h-screen overflow-hidden ${
      isDarkMode
        ? 'bg-gradient-to-br from-gray-900 via-black to-gray-800'
        : 'bg-white'
    }`}>
      {/* Title */}
      <div className="absolute top-4 md:top-8 left-1/2 transform -translate-x-1/2 z-20 text-center px-4">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent py-2"
        >
          My Workspace
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className={`text-xs sm:text-sm md:text-base ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {isMobile ? 'Tap objects to explore' : 'Click on any object to explore • Drag to rotate'}
        </motion.p>
      </div>

      {/* 3D Canvas */}
      <Canvas camera={{ position: getCameraPosition(), fov: isMobile ? 60 : 50 }}>
        <Scene activeObject={activeObject} setActiveObject={setActiveObject} isMobile={isMobile} isTablet={isTablet} isDarkMode={isDarkMode} />
      </Canvas>

      {/* Info Panel - Responsive */}
      <AnimatePresence>
        {activeObject && (
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : 300, y: isMobile ? 300 : 0 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: isMobile ? 0 : 300, y: isMobile ? 300 : 0 }}
            transition={{ type: "spring", damping: 25 }}
            className={`absolute ${
              isMobile
                ? 'bottom-4 left-4 right-4 w-auto'
                : 'top-20 md:top-24 right-4 md:right-8 w-72 sm:w-80'
            } ${
              isDarkMode
                ? 'bg-gray-900/95 border-gray-700'
                : 'bg-white/95 border-gray-200'
            } backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-6 border shadow-2xl z-30`}
          >
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2 md:gap-3">
                <span className="text-2xl md:text-3xl">{objectInfo[activeObject].icon}</span>
                <h3
                  className="text-base md:text-xl font-bold"
                  style={{ color: objectInfo[activeObject].color }}
                >
                  {objectInfo[activeObject].title}
                </h3>
              </div>
              <button
                onClick={() => setActiveObject(null)}
                className={`transition-colors ${
                  isDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className={`mb-4 md:mb-6 text-xs md:text-sm leading-relaxed ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {objectInfo[activeObject].description}
            </p>

            <div className="space-y-2 md:space-y-3">
              <h4 className={`text-xs font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {activeObject === 'database' || activeObject === 'cloud' ? 'Details' : 'Featured Projects'}
              </h4>
              {objectInfo[activeObject].projects.map((project, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800/50 hover:bg-gray-800'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: objectInfo[activeObject].color }}
                  />
                  <span className={`text-xs md:text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{project}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Guide - Hidden on mobile when info panel is open */}
      {(!activeObject || !isMobile) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className={`absolute ${
            isMobile ? 'bottom-4 left-4 right-4' : 'bottom-6 md:bottom-8 left-4 md:left-8'
          } z-20 backdrop-blur-xl rounded-lg md:rounded-xl p-3 md:p-4 border ${
            isDarkMode
              ? 'bg-gray-900/80 border-gray-700'
              : 'bg-white/80 border-gray-200'
          }`}
        >
          <h4 className={`text-xs font-semibold mb-2 md:mb-3 uppercase tracking-wider ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Interactive Objects
          </h4>
          <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'space-y-2'} text-xs md:text-sm ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <span>💻</span>
              <span className="truncate">Frontend</span>
            </div>
            <div className="flex items-center gap-2">
              <span>⚙️</span>
              <span className="truncate">Backend</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🔌</span>
              <span className="truncate">APIs</span>
            </div>
            <div className="flex items-center gap-2">
              <span>💻</span>
              <span className="truncate">Programming</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🌐</span>
              <span className="truncate">WordPress</span>
            </div>
            <div className="flex items-center gap-2">
              <span>💾</span>
              <span className="truncate">Database</span>
            </div>
            <div className="flex items-center gap-2">
              <span>☁️</span>
              <span className="truncate">Cloud</span>
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default Workspace3D;
